import { useEffect, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import { appMachine } from './machine';
import { useAppStore } from './store';
import { createSession, fetchConfig, fetchStats, fetchTrial, submitAnswer } from './lib/api';
import { getOrCreateDeviceKey, resetDeviceKey } from './lib/device';
import { EmotionWheel } from './components/EmotionWheel';
import { Metric } from './components/Metric';
import { StatsPanel } from './components/StatsPanel';
import type { EmotionLabel } from './types';
import './styles.css';

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function TurnstileBox({ siteKey, enabled, onToken }: { siteKey: string; enabled: boolean; onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      onToken('dev-token');
      return;
    }
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token: string) => onToken(token),
        'error-callback': () => onToken('')
      });
    };
    const timer = window.setInterval(() => {
      if (window.turnstile) {
        render();
        window.clearInterval(timer);
      }
    }, 150);
    render();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled, onToken, siteKey]);

  if (!enabled) return <div className="dev-human">Dev mode human pass enabled</div>;
  return <div className="turnstile-shell" ref={ref} />;
}

export default function App() {
  const [state, send] = useMachine(appMachine);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const config = useAppStore((s) => s.config);
  const deviceKey = useAppStore((s) => s.deviceKey);
  const deviceHashPreview = useAppStore((s) => s.deviceHashPreview);
  const verified = useAppStore((s) => s.verified);
  const trial = useAppStore((s) => s.trial);
  const answer = useAppStore((s) => s.answer);
  const guess = useAppStore((s) => s.guess);
  const stats = useAppStore((s) => s.stats);
  const error = useAppStore((s) => s.error);
  const setConfig = useAppStore((s) => s.setConfig);
  const setDeviceKey = useAppStore((s) => s.setDeviceKey);
  const setVerified = useAppStore((s) => s.setVerified);
  const setTrial = useAppStore((s) => s.setTrial);
  const setAnswer = useAppStore((s) => s.setAnswer);
  const setStats = useAppStore((s) => s.setStats);
  const setGuess = useAppStore((s) => s.setGuess);
  const setError = useAppStore((s) => s.setError);
  const clearForNext = useAppStore((s) => s.clearForNext);

  useEffect(() => {
    const boot = async () => {
      try {
        const [loadedConfig, localKey] = await Promise.all([fetchConfig(), Promise.resolve(getOrCreateDeviceKey())]);
        setConfig(loadedConfig);
        setDeviceKey(localKey);
        send({ type: 'READY' });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        send({ type: 'FAIL' });
      }
    };
    boot();
  }, [send, setConfig, setDeviceKey, setError]);

  const verify = async () => {
    if (!config) return;
    setLoading(true);
    setError(null);
    try {
      const session = await createSession({
        deviceKey,
        consentVersion: config.consentVersion,
        turnstileToken
      });
      setVerified(session.deviceHashPreview);
      send({ type: 'VERIFIED' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      send({ type: 'FAIL' });
    } finally {
      setLoading(false);
    }
  };

  const play = async () => {
    setLoading(true);
    setError(null);
    clearForNext();
    send({ type: 'PLAY' });
    try {
      const stimulus = await fetchTrial(deviceKey);
      setTrial(stimulus);
      send({ type: 'TRIAL' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      send({ type: 'FAIL' });
    } finally {
      setLoading(false);
    }
  };

  const answerNow = async () => {
    if (!trial || !guess.label) {
      setError('Pick the hidden emotion label first.');
      return;
    }
    setLoading(true);
    setError(null);
    send({ type: 'SUBMIT' });
    try {
      const result = await submitAnswer({
        deviceKey,
        stimulusId: trial.id,
        guessedLabel: guess.label,
        guessedValence: guess.valence,
        guessedArousal: guess.arousal,
        confidence: guess.confidence,
        responseMs: Math.max(100, Date.now() - guess.startedAt)
      });
      setAnswer(result);
      send({ type: 'FEEDBACK' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      send({ type: 'FAIL' });
    } finally {
      setLoading(false);
    }
  };

  const openStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await fetchStats();
      setStats(loaded);
      send({ type: 'STATS' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      send({ type: 'FAIL' });
    } finally {
      setLoading(false);
    }
  };

  const newIdentity = () => {
    const next = resetDeviceKey();
    setDeviceKey(next);
    window.location.reload();
  };

  const labels = config?.labels || [];

  return (
    <main className="app-shell">
      <div className="ambient ambient-a" />
      <div className="ambient ambient-b" />
      <header className="topbar">
        <div className="brand-mark">EC</div>
        <div>
          <p className="eyebrow">empathic accuracy lab</p>
          <h1>Empathy Calibration</h1>
        </div>
        <div className="topbar-actions">
          <span className={`status-dot ${verified ? 'on' : ''}`} />
          <span>{verified ? `anonymous ${deviceHashPreview}` : 'not verified'}</span>
          <button className="ghost small" onClick={openStats}>Commons</button>
          <a className="creator-link" href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer">LinkedIn</a>
          <a className="creator-link coffee" href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer">Buy me a coffee</a>
        </div>
      </header>

      {state.matches('booting') ? (
        <section className="panel hero-panel">
          <div className="loading-orb" />
          <h2>Loading the room…</h2>
          <p>Fetching the local rules, labels, and consent line.</p>
        </section>
      ) : null}

      {state.matches('consent') && config ? (
        <section className="panel hero-panel">
          <div className="section-kicker">The deal, legible in one line</div>
          <h2>Read the signal. Keep the commons.</h2>
          <p className="lede">{config.consentLine}</p>
          <div className="consent-card">
            <TurnstileBox siteKey={config.turnstileSiteKey} enabled={config.requiresTurnstile} onToken={setTurnstileToken} />
            <button onClick={verify} disabled={loading || (config.requiresTurnstile && !turnstileToken)}>
              {loading ? 'Verifying…' : 'I consent — start calibration'}
            </button>
          </div>
          <p className="fineprint">No login. No email. The server stores an irreversible hash of this browser’s local random key. Use “reset identity” any time.</p>
        </section>
      ) : null}

      {state.matches('idle') ? (
        <section className="panel hero-panel">
          <div className="hero-grid">
            <div>
              <div className="section-kicker">Grounded phenomenon: empathic accuracy</div>
              <h2>How accurately can you infer what another person is feeling?</h2>
              <p className="lede">You’ll read a subtle micro-scene, choose the hidden feeling, place it on a valence/arousal map, and size your confidence. The reward: a sharper self-model, not a dark pattern.</p>
              <div className="actions">
                <button onClick={play} disabled={loading}>{loading ? 'Loading…' : 'Begin round'}</button>
                <button className="ghost" onClick={openStats}>View commons</button>
              </div>
            </div>
            <div className="oracle-card">
              <EmotionWheel labels={labels} />
            </div>
          </div>
        </section>
      ) : null}

      {(state.matches('loadingTrial') || state.matches('submitting')) ? (
        <section className="panel hero-panel compact">
          <div className="loading-orb" />
          <h2>{state.matches('loadingTrial') ? 'Summoning a fresh emotional trace…' : 'Scoring your read…'}</h2>
          <p>Cloudflare Workers AI generates material; D1 caches it so the free tier breathes.</p>
        </section>
      ) : null}

      {state.matches('reading') && trial ? (
        <section className="panel trial-panel">
          <div className="trial-head">
            <span>{trial.source}</span>
            <span>ambiguity {Math.round(trial.ambiguity * 100)}%</span>
          </div>
          <h2>What is the person most likely feeling?</h2>
          <p className="context">{trial.speakerContext}</p>
          <article className="stimulus-card">{trial.text}</article>

          <div className="answer-layout">
            <div>
              <h3>1 · Choose the hidden emotion</h3>
              <div className="label-cloud">
                {labels.map((label) => (
                  <button
                    key={label}
                    className={guess.label === label ? 'selected' : ''}
                    onClick={() => setGuess({ label })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3>2 · Place it in affect space</h3>
              <EmotionWheel labels={labels} selected={guess.label} guess={{ valence: guess.valence, arousal: guess.arousal }} onSelect={(label) => setGuess({ label })} />
              <div className="slider-grid">
                <label>Valence <b>{guess.valence.toFixed(1)}</b><input type="range" min="-2" max="2" step="0.1" value={guess.valence} onChange={(e) => setGuess({ valence: Number(e.target.value) })} /></label>
                <label>Arousal <b>{guess.arousal.toFixed(1)}</b><input type="range" min="-2" max="2" step="0.1" value={guess.arousal} onChange={(e) => setGuess({ arousal: Number(e.target.value) })} /></label>
                <label>Confidence <b>{guess.confidence}%</b><input type="range" min="0" max="100" step="1" value={guess.confidence} onChange={(e) => setGuess({ confidence: Number(e.target.value) })} /></label>
              </div>
            </div>
          </div>

          <div className="actions sticky-actions">
            <button className="ghost" onClick={openStats}>Commons</button>
            <button onClick={answerNow} disabled={loading || !guess.label}>{loading ? 'Scoring…' : 'Lock read'}</button>
          </div>
        </section>
      ) : null}

      {state.matches('feedback') && answer && trial ? (
        <section className="panel feedback-panel">
          <div className="section-kicker">Calibration returned</div>
          <h2>{answer.score.verdict}</h2>
          <div className="metric-grid">
            <Metric label="round score" value={pct(answer.score.totalScore)} hint="whole signal" />
            <Metric label="label" value={answer.score.labelHit ? 'hit' : 'miss'} hint={`target: ${answer.truth.targetLabel}`} />
            <Metric label="affect-space" value={pct(answer.score.vaScore)} hint="valence + arousal" />
            <Metric label="confidence error" value={pct(answer.score.confidenceError)} hint="lower is better" />
          </div>

          <div className="feedback-grid">
            <div>
              <h3>Target signal</h3>
              <p className="context">{trial.speakerContext}</p>
              <article className="stimulus-card muted">{trial.text}</article>
              <p className="evidence"><strong>Why:</strong> {answer.truth.evidence}</p>
            </div>
            <div>
              <h3>Your map vs. target</h3>
              <EmotionWheel
                labels={labels}
                selected={guess.label}
                guess={{ valence: guess.valence, arousal: guess.arousal }}
                target={{ label: answer.truth.targetLabel, valence: answer.truth.targetValence, arousal: answer.truth.targetArousal }}
              />
              <div className="profile-card">
                <span>Your calibration style</span>
                <strong>{answer.profile.style}</strong>
                <small>{answer.profile.n} rounds · avg {pct(answer.profile.averageScore)} · label accuracy {pct(answer.profile.labelAccuracy)}</small>
              </div>
            </div>
          </div>

          <p className="commons-line">{answer.commonsContribution.line}</p>
          <div className="actions">
            <button className="ghost" onClick={openStats}>See commons</button>
            <button onClick={play}>Next signal</button>
          </div>
        </section>
      ) : null}

      {state.matches('stats') ? <StatsPanel stats={stats} onBack={() => send({ type: 'BACK' })} onPlay={play} /> : null}

      {state.matches('error') ? (
        <section className="panel error-panel">
          <div className="section-kicker">Something snagged</div>
          <h2>The app is intact; the edge said no.</h2>
          <p>{error || 'Unknown error.'}</p>
          <div className="actions">
            <button onClick={() => { setError(null); send({ type: verified ? 'RETRY' : 'BOOT' }); }}>Try again</button>
            <button className="ghost" onClick={newIdentity}>Reset identity</button>
          </div>
        </section>
      ) : null}

      <footer>
        <button className="text-button" onClick={newIdentity}>reset identity</button>
        <span>Anonymous device-keyed · Turnstile human gate · aggregate commons</span>
      </footer>
    </main>
  );
}
